import React from 'react';

interface DynamicTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  minRows?: number;
  wordLimit?: number;
}

export const DynamicTextarea = React.memo(function DynamicTextarea({
  value,
  onChange,
  placeholder,
  className,
  minRows = 5,
  wordLimit = 100,
}: DynamicTextareaProps) {
  // Calculate word count - debounced for performance
  const wordCount = React.useMemo(() => {
    if (!value.trim()) return 0;
    return value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  }, [value]);

  // Handle input with word limit enforcement
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const newWordCount = newValue
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;

      // Allow typing if under word limit or if deleting content
      if (newWordCount <= wordLimit || newValue.length < value.length) {
        onChange(e);
      }
      // If over limit, prevent the change (but don't call onChange)
    },
    [onChange, value.length, wordLimit]
  );

  // Handle paste events
  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const currentValue = (e.target as HTMLTextAreaElement).value;
      const selectionStart = (e.target as HTMLTextAreaElement).selectionStart;
      const selectionEnd = (e.target as HTMLTextAreaElement).selectionEnd;

      // Calculate what the text would be after paste
      const beforeSelection = currentValue.substring(0, selectionStart);
      const afterSelection = currentValue.substring(selectionEnd);
      const newValue = beforeSelection + pastedText + afterSelection;
      const newWordCount = newValue
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;

      // If paste would exceed word limit, allow it but let the navigation check handle it
      // This allows the fallback behavior you requested
      if (newWordCount > wordLimit) {
        // Don't prevent the paste, let it happen
        return;
      }
    },
    [wordLimit]
  );

  // Calculate dynamic rows based on content and word count
  const calculateRows = React.useMemo(() => {
    // Start with minimum rows
    let rows = minRows;

    // Add rows based on word count progression toward limit
    if (wordCount > 0) {
      const progressRatio = Math.min(wordCount / wordLimit, 1);
      const additionalRows = Math.floor(progressRatio * 8); // Up to 8 additional rows
      rows = minRows + additionalRows;
    }

    // Also consider line breaks in the content
    const lineBreaks = (value.match(/\n/g) || []).length;
    const contentRows = lineBreaks + Math.ceil(value.length / 60); // Rough estimate: 60 chars per line

    // Use the larger of the two calculations
    return Math.max(rows, Math.min(contentRows, minRows + 8));
  }, [value, wordCount, wordLimit, minRows]);

  return (
    <textarea
      value={value}
      onChange={handleChange}
      onPaste={handlePaste}
      placeholder={placeholder}
      className={`${className} resize-none`}
      rows={calculateRows}
      style={{
        transition: 'height 0.2s ease-in-out',
      }}
    />
  );
});
